import { FormEvent, useEffect, useState } from 'react';
import type { CategoryRef, ProductCard as Card, ProductBarcodeInput, ProductPatch, SaleType, VatMode } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatHs, VatCheckBadge } from '../components/VatCheckBadge';
import { formatDate, formatMoney, formatQty, SALE_TYPE_LABEL, UNIT_LABEL, VAT_LABEL } from '../format';

interface Props {
  /** null — yeni məhsul */
  id: string | null;
  canEdit: boolean;
  onBack: () => void;
  onSaved: (id: string) => void;
}

const VAT_MODES: VatMode[] = ['vat18', 'vat0', 'exempt', 'agri'];
const SALE_TYPES: SaleType[] = ['piece', 'weight', 'measure', 'service'];
const UNITS_FOR: Record<SaleType, string[]> = {
  piece: ['pcs', 'bag', 'box'],
  weight: ['kg', 'g', 't'],
  measure: ['l', 'm', 'm2'],
  service: ['svc'],
};

// Məhsul kartı: baxış və (icazə varsa) redaktə. Yeni məhsul — id = null.
export function ProductCard({ id, canEdit, onBack, onSaved }: Props) {
  const [card, setCard] = useState<Card | null>(null);
  const [categories, setCategories] = useState<CategoryRef[]>([]);
  const [editing, setEditing] = useState(id === null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // form sahələri
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [vatMode, setVatMode] = useState<VatMode>('vat18');
  // XİF MN kodu (KT-69): ƏDV yoxlaması bu kodla; əl ilə yazılan kod «manual» mənbəli
  const [hsCode, setHsCode] = useState('');
  const [retailPrice, setRetailPrice] = useState('');
  const [active, setActive] = useState(true);
  const [saleType, setSaleType] = useState<SaleType>('piece');
  const [unitCode, setUnitCode] = useState('pcs');
  const [factoryBarcode, setFactoryBarcode] = useState('');
  // Qutu barkodu (KT-101): 1 qutu = N vahid; kassada qutu skan ediləndə səbətə N düşür
  const [boxBarcode, setBoxBarcode] = useState('');
  const [boxQty, setBoxQty] = useState('');
  // Yeni kateqoriya (KT-101): formadan çıxmadan yaradılır və seçilir
  const [newCat, setNewCat] = useState<string | null>(null);
  async function addCategory() {
    try {
      const c = await api.productCategoryCreate((newCat ?? '').trim());
      setCategories((cur) => [...cur, c].sort((a, b) => a.name.localeCompare(b.name, 'az')));
      setCategoryId(c.id);
      setNewCat(null);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kateqoriya yaradılmadı');
    }
  }

  useEffect(() => {
    api.productCategories().then(setCategories).catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    if (id === null) return;
    let cancelled = false;
    setEditing(false);
    api
      .productCard(id)
      .then((c) => {
        if (cancelled) return;
        setCard(c);
        fill(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Kart yüklənmədi');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  function fill(c: Card) {
    setName(c.name);
    setCategoryId(c.categoryId ?? '');
    setVatMode(c.vatMode);
    setHsCode(c.hsCode ? formatHs(c.hsCode) : '');
    setRetailPrice(c.retailPrice === null ? '' : String(c.retailPrice));
    setActive(c.active);
  }

  function cancelEdit() {
    if (card) {
      fill(card);
      setEditing(false);
      setError(null);
    } else {
      onBack();
    }
  }

  async function save(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const price = retailPrice.trim() === '' ? null : Number(retailPrice.replace(',', '.'));
    if (price !== null && (!Number.isFinite(price) || price < 0)) {
      setError('Qiymət düzgün deyil');
      return;
    }
    const box: ProductBarcodeInput[] = boxBarcode.trim() ? [{ barcode: boxBarcode.trim(), kind: 'box', qty: Number(boxQty) }] : [];
    if (box.length > 0 && !(Number.isInteger(Number(boxQty)) && Number(boxQty) >= 2)) {
      setError('Qutuda neçə vahid olduğunu yazın (2 və çox, tam ədəd)');
      return;
    }
    setBusy(true);
    try {
      if (id === null) {
        const barcodes: ProductBarcodeInput[] = [...(factoryBarcode.trim() ? [{ barcode: factoryBarcode.trim(), kind: 'factory' as const }] : []), ...box];
        const created = await api.productCreate({
          name: name.trim(),
          categoryId: categoryId || null,
          saleType,
          unitCode,
          vatMode,
          retailPrice: price,
          barcodes,
        });
        onSaved(created.id);
      } else {
        const hsDigits = hsCode.replace(/[\s.]/g, '');
        const patch: ProductPatch = {
          name: name.trim(),
          categoryId: categoryId || null,
          vatMode,
          // Dəyişməyən kod göndərilmir — mənbəsi (sənəd, Excel) «əl ilə»yə çevrilməsin
          ...(hsDigits !== (card?.hsCode ?? '') ? { hsCode: hsDigits === '' ? null : hsDigits } : {}),
          active,
          retailPrice: price,
          ...(box.length > 0 ? { addBarcodes: box } : {}),
        };
        await api.productPatch(id, patch);
        setBoxBarcode('');
        setBoxQty('');
        const fresh = await api.productCard(id);
        setCard(fresh);
        fill(fresh);
        setEditing(false);
        onSaved(id);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Saxlanılmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  const title = id === null ? 'Yeni məhsul' : (card?.name ?? 'Məhsul');

  return (
    <div className="shell">
      <header className="topbar">
        <button type="button" className="link-btn" onClick={onBack}>
          ← Məhsullar
        </button>
        <span className="brand">{title}</span>
        <span className="topbar-spacer" />
        {!editing && canEdit && card && (
          <button type="button" className="btn-small" onClick={() => setEditing(true)}>
            Dəyiş
          </button>
        )}
      </header>

      <main className={editing ? 'page page-form' : 'page narrow'}>
        {error && (
          <p className="bad" role="alert">
            {error}
          </p>
        )}
        {id !== null && !card && !error && <p className="muted">Yüklənir…</p>}

        {editing && (
          // Böyük forma iki sütunla (KT-99): solda təyinat, sağda qiymət və vergi; məcburi sahə — qırmızı *
          <form className="product-form form-2col" onSubmit={save} noValidate>
            <section className="card form-col" aria-labelledby="pf-main">
              <h2 id="pf-main">Məhsulun təyini</h2>
              <label className="field">
                <span>
                  Məhsulun adı <span className="req" aria-hidden="true">*</span>
                </span>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required disabled={busy} />
              </label>

              <label className="field">
                Kateqoriya
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} disabled={busy}>
                  <option value="">— yoxdur —</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              {newCat === null ? (
                <button type="button" className="link-btn" onClick={() => setNewCat('')} disabled={busy}>
                  + Yeni kateqoriya
                </button>
              ) : (
                <div className="toolbar">
                  <input aria-label="Yeni kateqoriyanın adı" value={newCat} onChange={(e) => setNewCat(e.target.value)} maxLength={80} autoFocus />
                  <button type="button" className="btn-small" onClick={() => void addCategory()} disabled={busy || newCat.trim().length === 0}>
                    Əlavə et
                  </button>
                  <button type="button" className="link-btn" onClick={() => setNewCat(null)}>
                    Ləğv et
                  </button>
                </div>
              )}

              {id === null && (
                <>
                  <label className="field">
                    Necə satılır?
                    <select
                      value={saleType}
                      onChange={(e) => {
                        const st = e.target.value as SaleType;
                        setSaleType(st);
                        setUnitCode(UNITS_FOR[st][0]);
                      }}
                      disabled={busy}
                    >
                      {SALE_TYPES.map((st) => (
                        <option key={st} value={st}>
                          {SALE_TYPE_LABEL[st]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    <span>
                      Ölçü vahidi <span className="req" aria-hidden="true">*</span>
                    </span>
                    <select value={unitCode} onChange={(e) => setUnitCode(e.target.value)} disabled={busy}>
                      {UNITS_FOR[saleType].map((u) => (
                        <option key={u} value={u}>
                          {UNIT_LABEL[u]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="field">
                    İstehsalçı barkodu (istəyə görə)
                    <input
                      inputMode="numeric"
                      value={factoryBarcode}
                      onChange={(e) => setFactoryBarcode(e.target.value)}
                      placeholder="8–14 rəqəm"
                      disabled={busy}
                    />
                  </label>
                </>
              )}
              <div className="form-row-2">
                <label className="field">
                  Qutu barkodu (istəyə görə)
                  <input value={boxBarcode} onChange={(e) => setBoxBarcode(e.target.value)} maxLength={32} disabled={busy} />
                </label>
                <label className="field">
                  Qutuda neçə vahid
                  <input inputMode="numeric" value={boxQty} onChange={(e) => setBoxQty(e.target.value)} placeholder="məs. 12" disabled={busy || !boxBarcode.trim()} />
                </label>
              </div>

              {id !== null && (
                <label className="check">
                  <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={busy} />
                  Satışda (passiv olsa, kassada görünmür)
                </label>
              )}
            </section>

            <section className="card form-col" aria-labelledby="pf-price">
              <h2 id="pf-price">Qiymət və vergi</h2>
              <label className="field">
                <span>
                  ƏDV rejimi <span className="req" aria-hidden="true">*</span>
                </span>
                <select value={vatMode} onChange={(e) => setVatMode(e.target.value as VatMode)} disabled={busy}>
                  {VAT_MODES.map((v) => (
                    <option key={v} value={v}>
                      {VAT_LABEL[v]}
                    </option>
                  ))}
                </select>
              </label>

              {id !== null && (
                <label className="field">
                  <span>XİF MN kodu</span>
                  <input
                    inputMode="numeric"
                    value={hsCode}
                    onChange={(e) => setHsCode(e.target.value)}
                    placeholder="məs. 3105 20 100 0"
                    maxLength={16}
                    disabled={busy}
                    aria-describedby="pf-hs-hint"
                  />
                  <span id="pf-hs-hint" className="muted small">
                    10 rəqəm (natamam: 4, 6 və ya 8). ƏDV növü bu koda görə yoxlanır.
                  </span>
                </label>
              )}

              <label className="field">
                Pərakəndə satış qiyməti, ₼
                <input
                  inputMode="decimal"
                  value={retailPrice}
                  onChange={(e) => setRetailPrice(e.target.value)}
                  placeholder="məs. 2,10"
                  disabled={busy}
                />
              </label>

              <p className="muted small">Açılış qalığı «Mal və anbar → Alış» və ya Excel idxalı ilə yazılır.</p>
            </section>

            <div className="form-actions form-actions-wide">
              <button type="button" className="btn-small" onClick={cancelEdit} disabled={busy}>
                Ləğv et
              </button>
              <button type="submit" className="btn-primary" disabled={busy || name.trim().length === 0}>
                {busy ? 'Saxlanılır…' : 'Qeydə al'}
              </button>
            </div>
          </form>
        )}

        {!editing && card && (
          <div className="product-view">
            <dl className="facts">
              <dt>Kateqoriya</dt>
              <dd>{card.categoryName ?? '—'}</dd>
              <dt>Necə satılır</dt>
              <dd>
                {SALE_TYPE_LABEL[card.saleType]} · {UNIT_LABEL[card.unitCode] ?? card.unitCode}
              </dd>
              <dt>ƏDV</dt>
              <dd>
                {VAT_LABEL[card.vatMode]} {card.vatCheck && <VatCheckBadge level={card.vatCheck.level} />}
                {card.vatCheck && card.vatCheck.level !== 'green' && <span className="muted small vat-check-note">{card.vatCheck.title}</span>}
                {card.vatCheck?.expiry && <span className="muted small vat-check-note">{card.vatCheck.expiry}</span>}
              </dd>
              <dt>XİF MN</dt>
              <dd className="mono">{card.hsCode ? formatHs(card.hsCode) : '—'}</dd>
              <dt>Status</dt>
              <dd>{card.active ? 'Satışda' : 'Passiv'}</dd>
              <dt>Pərakəndə qiymət</dt>
              <dd className="mono">{formatMoney(card.retailPrice)}</dd>
              <dt>Ümumi qalıq</dt>
              <dd className="mono">{formatQty(card.qtyPhys)}</dd>
              <dt>Son dəyişiklik</dt>
              <dd>{formatDate(card.updatedAt)}</dd>
            </dl>

            <section>
              <h2 className="sub">Barkodlar</h2>
              {card.barcodes.length === 0 && <p className="muted">Barkod yoxdur (kassada əl ilə seçilir).</p>}
              <ul className="plain">
                {card.barcodes.map((b) => (
                  <li key={b.barcode} className="mono">
                    {b.barcode} <span className="muted small">{b.kind === 'factory' ? 'istehsalçı' : 'öz kodu'}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="sub">Qiymətlər</h2>
              {card.prices.length === 0 && <p className="muted">Qiymət təyin olunmayıb.</p>}
              <ul className="plain">
                {card.prices.map((p) => (
                  <li key={p.priceTypeCode}>
                    {p.priceTypeName}: <span className="mono">{formatMoney(p.price)}</span>
                  </li>
                ))}
              </ul>
            </section>

            <section>
              <h2 className="sub">Anbar</h2>
              {card.stock.length === 0 && <p className="muted">Qalıq yoxdur.</p>}
              <ul className="plain">
                {card.stock.map((s) => (
                  <li key={s.warehouseId}>
                    {s.warehouseName}: <span className="mono">{formatQty(s.qtyPhys)}</span>
                    {s.qtyReserved > 0 && <span className="muted small"> (rezerv {formatQty(s.qtyReserved)})</span>}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
